import { motion, AnimatePresence } from 'motion/react';
import styles from './ResumeDraftModal.module.scss';

interface ResumeDraftModalProps {
  isOpen: boolean;
  lastSaved: Date | null;
  questionCount: number;
  onResume: () => void;
  onDiscard: () => void;
}

export function ResumeDraftModal({
  isOpen,
  lastSaved,
  questionCount,
  onResume,
  onDiscard,
}: ResumeDraftModalProps) {
  const formatDate = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles.modal__backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onDiscard}
          />

          {/* Modal */}
          <motion.div
            className={styles.modal}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <div className={styles.modal__icon}>💾</div>

            <h2 className={styles.modal__title}>Resume Draft?</h2>

            <p className={styles.modal__message}>
              You have an unsaved draft from{' '}
              <strong>{lastSaved ? formatDate(lastSaved) : 'earlier'}</strong>
            </p>

            <div className={styles.modal__details}>
              <div className={styles.modal__detail}>
                <span className={styles.modal__detailLabel}>Questions:</span>
                <span className={styles.modal__detailValue}>{questionCount}</span>
              </div>
            </div>

            <div className={styles.modal__actions}>
              <motion.button
                className={styles.modal__resumeButton}
                onClick={onResume}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Resume Editing
              </motion.button>
              <motion.button
                className={styles.modal__discardButton}
                onClick={onDiscard}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Fresh
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
